'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef } from 'react';
import Fuse from 'fuse.js';
import { AnimatePresence, motion } from 'motion/react';
import { SearchAccordion } from '~/components/primitives/Accordion';
import { ButtonLink } from '~/components/primitives';
import { useQueryState, parseAsString } from 'nuqs';
import { useI18n } from '~/features/i18n';
import { I18N_CONF } from '@funkey/locales/config';
import { useFaqSearchResults } from './FaqSearchContext';
import { usePostHog } from 'posthog-js/react';

/**
 * Renders combined FAQ search results as a standalone section.
 * This is placed outside the header to avoid pushing the header taller.
 */
export default function FaqSearchResults() {
  const $ = _c(25);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = parseAsString.withDefault("").withOptions({
      shallow: true,
      throttleMs: 300
    });
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [query] = useQueryState("q", t0);
  const {
    allItems
  } = useFaqSearchResults();
  const i18n = useI18n();
  const locale = i18n?.locale ?? "nl-BE";
  const posthog = usePostHog();
  const lastTrackedQuery = useRef("");
  let t1;
  if ($[1] !== query) {
    t1 = query.trim();
    $[1] = query;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isSearching = t1.length > 0;
  let t2;
  if ($[3] !== allItems) {
    t2 = new Fuse(allItems, {
      keys: ["title", "description"],
      threshold: 0.4,
      ignoreLocation: true,
      includeScore: true
    });
    $[3] = allItems;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const fuse = t2;
  let t3;
  if ($[5] !== allItems || $[6] !== fuse || $[7] !== query) {
    bb0: {
      const trimmed = query.trim();
      if (!trimmed || allItems.length === 0) {
        let t4;
        if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = [];
          $[9] = t4;
        } else {
          t4 = $[9];
        }
        t3 = t4;
        break bb0;
      }
      t3 = fuse.search(trimmed).map(_temp);
    }
    $[5] = allItems;
    $[6] = fuse;
    $[7] = query;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const filteredItems = t3;
  let t4;
  let t5;
  if ($[10] !== filteredItems.length || $[11] !== locale || $[12] !== posthog || $[13] !== query) {
    t4 = () => {
      const trimmed_0 = query.trim();
      if (!trimmed_0) {
        lastTrackedQuery.current = "";
        return;
      }
      if (trimmed_0 === lastTrackedQuery.current) {
        return;
      }
      const timeout = setTimeout(() => {
        lastTrackedQuery.current = trimmed_0;
        posthog.capture("faq.search", {
          query: trimmed_0,
          results_count: filteredItems.length,
          locale
        });
      }, 1000);
      return () => clearTimeout(timeout);
    };
    t5 = [query, filteredItems.length, locale, posthog];
    $[10] = filteredItems.length;
    $[11] = locale;
    $[12] = posthog;
    $[13] = query;
    $[14] = t4;
    $[15] = t5;
  } else {
    t4 = $[14];
    t5 = $[15];
  }
  useEffect(t4, t5);
  const matchedUid = filteredItems.length === 1 ? filteredItems[0]?.uid ?? null : null;
  let t6;
  if ($[16] !== filteredItems || $[17] !== i18n || $[18] !== isSearching || $[19] !== locale || $[20] !== matchedUid || $[21] !== query) {
    t6 = isSearching && <motion.section key="faq-search-results" data-testid="faq-search-results" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: "easeOut"
    }} className="-mt-8 bg-primary pt-4 pb-12"><div className="container max-w-3xl">{filteredItems.length > 0 ? <SearchAccordion key={query.trim()} items={filteredItems} color="light" highlightQuery={query} forceOpenUid={matchedUid} /> : <div data-testid="faq-no-results" className="flex flex-col items-start gap-4 py-6"><p className="text-lg text-white/70">{i18n._("faq.noResults", undefined, {
              message: "No resulsts found. Do you have any more questions?"
            })}</p><ButtonLink href={I18N_CONF.getTranslatedUrl("contact", {
            locale
          })} variant="secondary" size="md">{i18n._("faq.contactUs", undefined, {
              message: "Contact us"
            })}</ButtonLink></div>}</div></motion.section>;
    $[16] = filteredItems;
    $[17] = i18n;
    $[18] = isSearching;
    $[19] = locale;
    $[20] = matchedUid;
    $[21] = query;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== t6) {
    t7 = <AnimatePresence>{t6}</AnimatePresence>;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  return t7;
}
function _temp(r) {
  return r.item;
}